import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { catalogNameKey, type CatalogItem, type Group, type Store } from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { formatQty, itemsCount, joinNames, plural } from '../lib/format.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Sheet } from './Sheet.tsx'
import { Button, Checkbox, Label, Paragraph, RadioCard, Spinner, TextCol } from './ui.tsx'

interface Props {
  open: boolean
  onClose(): void
  /** Zdrojova skupina — ta, jejiz sablona je otevrena. */
  groupId: string
  items: readonly CatalogItem[]
  /** Obchody zdrojove skupiny vcetne skrytych. */
  stores: readonly Store[]
  /** Sablona jina nez Pravidelny nakup: v cilove skupine vznikne stejnojmenna. */
  fromTemplateId?: string | undefined
}

const offline = (err: unknown) => err instanceof HttpError && err.isOffline

/**
 * Kopie sablony do jine skupiny: kam a ktere polozky, nic vic. Obchody resi
 * server — najde v cilove skupine stejny, nebo ho zalozi; prejmenovat nebo
 * smazat jde potom. Jednorazova kopie, dalsi zmeny se mezi sablonami neprenaseji.
 */
export function CopyTemplateSheet({ open, onClose, ...rest }: Props) {
  return (
    <Sheet open={open} onClose={onClose} title="Kopírovat šablonu" subtitle="Do šablony jiné skupiny">
      <CopyBody {...rest} onClose={onClose} />
    </Sheet>
  )
}

interface Target {
  group: Group
  /** Nazvy polozek, ktere cilova sablona uz ma. */
  existing: Set<string>
}

function CopyBody({ groupId, items, stores, fromTemplateId, onClose }: Omit<Props, 'open'>) {
  const s = useSignedIn()
  const navigate = useNavigate()
  const others = s.groups.filter((g) => g.id !== groupId)
  const [targetId, setTargetId] = useState(() => (others.find((g) => !g.personal) ?? others[0])?.id ?? null)
  const [target, setTarget] = useState<Target | null>(null)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ group: Group; created: number; skipped: number; stores: string[] } | null>(null)

  // Po vyberu skupiny se nacte jeji sablona: co uz tam je, se predem odskrtne.
  useEffect(() => {
    const group = s.groups.find((g) => g.id === targetId)
    if (!group) return
    let cancelled = false
    setTarget(null)
    setLoadError(null)
    api.catalog
      .list(group.id)
      .then((targetItems) => {
        if (cancelled) return
        const existing = new Set(targetItems.map((i) => catalogNameKey(i.name)))
        setTarget({ group, existing })
        setSelected(new Set(items.filter((i) => !existing.has(catalogNameKey(i.name))).map((i) => i.id)))
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(offline(err) ? 'Šablonu jde kopírovat jen se signálem.' : errorMessage(err))
      })
    return () => {
      cancelled = true
    }
    // Polozky zdroje se behem otevreneho panelu nemeni.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId])

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function copy() {
    if (!target || selected.size === 0) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.catalog.copy(target.group.id, {
        fromGroupId: groupId,
        itemIds: [...selected],
        ...(fromTemplateId ? { fromTemplateId } : {}),
      })
      setDone({
        group: target.group,
        created: result.created.length,
        skipped: result.skipped,
        stores: result.createdStores,
      })
    } catch (err) {
      setError(offline(err) ? 'Šablonu jde kopírovat jen se signálem.' : errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="stack">
        <Paragraph tone="fg">
          {done.created > 0
            ? `Do šablony skupiny ${done.group.name} ${plural(done.created, 'přibyla', 'přibyly', 'přibylo')} ${itemsCount(done.created)}.`
            : `V šabloně skupiny ${done.group.name} nic nepřibylo.`}
          {done.skipped > 0 && ` ${itemsCount(done.skipped)} už tam ${done.skipped === 1 ? 'byla' : 'byly'}.`}
        </Paragraph>
        {done.stores.length > 0 && (
          <Paragraph>{`Přibyl${done.stores.length === 1 ? '' : 'y'} i ${done.stores.length === 1 ? 'obchod' : 'obchody'} ${joinNames(done.stores)}.`}</Paragraph>
        )}
        <Button
          kind="primary"
          full
          onClick={() => {
            s.selectGroup(done.group.id)
            onClose()
            // Adresa puvodni sablony v cilove skupine neexistuje — na seznam jejich sablon.
            navigate('/sablony')
          }}
        >
          {`Otevřít šablonu skupiny ${done.group.name}`}
        </Button>
        <Button full onClick={onClose}>
          Zavřít
        </Button>
      </div>
    )
  }

  if (others.length === 0) {
    return <Paragraph>Jsi jen v jedné skupině. Založ nebo přijmi pozvánku do další a šablonu do ní zkopíruješ.</Paragraph>
  }

  // Polozky podle obchodu jako na strance Sablona; ze skryteho obchodu jsou „Bez obchodu".
  const activeStores = stores.filter((store) => !store.archived).sort((a, b) => a.position - b.position)
  const byName = (a: CatalogItem, b: CatalogItem) => a.name.localeCompare(b.name, 'cs')
  const sections = [
    ...activeStores.map((store) => ({
      key: store.id,
      title: store.name,
      items: items.filter((i) => i.storeId === store.id),
    })),
    {
      key: 'none',
      title: 'Bez obchodu',
      items: items.filter((i) => !i.storeId || !activeStores.some((store) => store.id === i.storeId)),
    },
  ]
    .filter((section) => section.items.length > 0)
    .map((section) => ({ ...section, items: [...section.items].sort(byName) }))
  const selectable = target ? items.filter((i) => !target.existing.has(catalogNameKey(i.name))) : []

  return (
    <div className="stack">
      <div className="stack-8" role="radiogroup" aria-label="Kam kopírovat">
        <Label as="span">Kam</Label>
        {others.map((group) => (
          <RadioCard
            key={group.id}
            icon="users"
            title={group.name}
            description={group.personal ? 'Osobní skupina' : group.members.map((m) => m.name).join(', ')}
            selected={group.id === targetId}
            onSelect={() => setTargetId(group.id)}
          />
        ))}
      </div>

      {loadError && <Paragraph tone="error">{loadError}</Paragraph>}
      {!target && !loadError && <Spinner />}

      {target && (
        <div className="stack-8">
          <div className="copy-items-head">
            <Label as="span">{`Položky · vybráno ${selected.size} z ${selectable.length}`}</Label>
            {selectable.length > 0 && (
              <Button
                kind="quiet"
                compact
                onClick={() =>
                  setSelected(selected.size === selectable.length ? new Set() : new Set(selectable.map((i) => i.id)))
                }
              >
                {selected.size === selectable.length ? 'Zrušit výběr' : 'Vybrat vše'}
              </Button>
            )}
          </div>
          {sections.map((section) => (
            <div key={section.key} className="stack-8">
              <span className="field-label">{section.title}</span>
              {section.items.map((item) => {
                const exists = target.existing.has(catalogNameKey(item.name))
                const checked = !exists && selected.has(item.id)
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`select-row select-row-pad-even${checked ? ' is-selected' : ''}`}
                    aria-pressed={checked}
                    disabled={exists}
                    onClick={() => toggle(item.id)}
                  >
                    <Checkbox checked={checked} onRow />
                    <TextCol
                      title={item.name}
                      sub={exists ? 'Už ve skupině je' : formatQty(item.defaultQty, item.unit)}
                      subTone={exists ? 'muted' : undefined}
                    />
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}

      {error && <Paragraph tone="error">{error}</Paragraph>}
      {target && (
        <Button kind="primary" full disabled={busy || selected.size === 0} onClick={() => void copy()}>
          {selected.size === 0 ? 'Nic není vybráno' : `Zkopírovat ${plural(selected.size, 'položku', 'položky', 'položek')}`}
        </Button>
      )}
    </div>
  )
}
